import React, { FC } from 'react'
import styled, { keyframes, css } from 'styled-components'

const ANIMATION_TIME = 1000

const LOADING_ANIMATION_BEFORE = keyframes`
  0% {
      transform: translate3d(0, 0, 0);
  }

  50% {
      transform: translate3d(-100%, 0, 0);
  }

  100% {
      transform: translate3d(0, 0, 0);
  }
`

const LOADING_ANIMATION = keyframes`
  0% {
     transform: translate3d(-50%, 0, 0);
  }

  50% {
    transform: translate3d(0, 0, 0);  
  }

  100% {
    transform: translate3d(-50%, 0, 0);
  }
`

type Size = '16px' | '24px' | '64px'

export interface LoadingProps {
  size?: Size
  primary?: boolean
  className?: string
}

const BAR_HEIGHT = {
  '16px': '4px',
  '24px': '6px',
  '64px': '8px',
} as const

const INNER_BAR_WIDTH = {
  '16px': '8px',
  '24px': '10px',
  '64px': '20px',
}

interface LoadingAnimationWrapperProps extends LoadingProps {
  'data-testid': string
  $mountDelay: number
  $primary?: boolean
  $size: Size
}

const BarWrapper = styled.span`
  width: 100%;
  border-radius: 999px;
  overflow: hidden;

  /* This is needed to fix a safari bug with border-radius */
  /* https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b */
  /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
  -webkit-mask-image: -webkit-radial-gradient(white, black);
  /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
`

const Bar = styled.span<{ $size: Size }>`
  width: 100%;
  display: block;
  height: 1em;
  position: relative;

  padding-right: 100%;
  padding-left: 100%;
  transform: translate3d(-50%, 0, 0);
  animation: ${LOADING_ANIMATION} ${ANIMATION_TIME}ms linear infinite;

  ::before {
    width: 100%;
    position: absolute;

    left: 0;
    top: 0;

    content: '';
    display: inline-block;
    height: 1em;
    opacity: 0.15;

    background: currentColor;
  }

  ::after {
    width: ${({ $size }) => INNER_BAR_WIDTH[$size]};
    border-radius: 999px;

    content: '';
    display: inline-block;
    height: 1em;
    background: currentColor;

    animation: ${LOADING_ANIMATION_BEFORE} ${ANIMATION_TIME}ms linear infinite;
  }
`

const LoadingAnimationComponent = styled.span<LoadingAnimationWrapperProps>(
  ({ theme: { colors }, $primary, $mountDelay, $size }) => css`
    display: inline-flex;
    width: ${$size};
    font-size: ${BAR_HEIGHT[$size]};
    aspect-ratio: 1;

    line-height: 1;

    align-items: center;

    overflow: hidden;

    color: ${$primary
      ? colors.foreground.primary[1]
      : colors.foreground.secondary[4]};

    ${Bar} {
      animation-delay: ${$mountDelay}ms;

      :after {
        animation-delay: ${$mountDelay}ms;
      }
    }
  `
)

const LoadingAnimation: FC<LoadingProps> = ({
  className,
  size = '24px',
  primary = false,
  ...props
}) => {
  /**
   * We capture when this component was mounted. This way we can apply a delay
   * to the animation based on that time and make all Loading animations in the app to be
   * in sync.
   *
   * Imagine animation lasts 100ms:
   *
   * 0                 100ms     150ms    200ms
   * | ----------------- | ----------------- |
   *                               |
   *                             mount
   *
   * We configure the animation to start on the previous cycle: 100ms, so we
   * set the animation-delay to -50ms. Finally, we use negative numbers to make
   * the component animate right away (instead of waiting for the next cycle).
   */
  const mountTime = React.useRef(Date.now())
  const mountDelay = -(mountTime.current % ANIMATION_TIME)

  return (
    <LoadingAnimationComponent
      className={className}
      data-testid="loading-animation"
      $primary={primary}
      $mountDelay={mountDelay}
      $size={size}
      {...props}>
      <BarWrapper>
        <Bar $size={size} />
      </BarWrapper>
      <span className="sr-only">Loading Placeholder icon</span>
    </LoadingAnimationComponent>
  )
}

export default LoadingAnimation
